<template>
  <DashboardLayout>
    <div class="d-flex align-items-center gap-2 mb-4 page-identifier">
      <p class="mb-0 main-identifier fw-semibold">Cashbook</p>
    </div>

    <div class="d-flex justify-content-center">
      <div class="form-outer" style="max-width: 1400px;">
        <div class="view-item position-relative">

          <div class="form-inner">
            <div class="form-header d-flex justify-content-between align-items-center report">
              <div>
                <h1 class="mb-0">Cashbook</h1>
                <p class="mb-0 small">Track all financial transactions with detailed deduction breakdowns.
                </p>
              </div>
            </div>

            <div class="form-body cashbook-content-area position-relative">
              <div v-if="loadingCashbook" class="loading-overlay text-center">
                <div class="spinner-border text-primary" role="status" aria-hidden="true"></div>
                <p class="mt-2 mb-0 text-muted">Loading Cashbook...</p>
              </div>

              <div v-if="errorMessage && !loadingCashbook" class="alert alert-warning" role="alert">
                {{ errorMessage }}
              </div>

              <!-- Filters -->
              <div class="filters-section mb-4">
                <div class="row col-md-12 g-3">
                  <div class="col-md-3">
                    <label class="form-label small fw-semibold">MDA:</label>
                    <SearchableSelect :model-value="filters.mda" @update:model-value="onMdaFilterChange"
                      :options="mdaFilterOptions" placeholder="All MDA" small clearable />
                  </div>

                  <div class="col-md-3">
                    <label class="form-label small fw-semibold">Bank:</label>
                    <select class="form-select form-select-sm" v-model="filters.bank" @change="onBankChange">
                      <option value="">Select Bank</option>
                      <option v-for="bank in uniqueBanks" :key="bank.id" :value="bank.name">{{ bank.name }}</option>
                    </select>
                  </div>

                  <div class="col-md-2">
                    <label class="form-label small fw-semibold">Month:</label>
                    <select class="form-select form-select-sm" v-model="filters.month">
                      <option value="">Select Month</option>
                      <option v-for="month in months" :key="month.value" :value="month.value">{{ month.label }}</option>
                    </select>
                  </div>

                  <div class="col-md-2">
                    <label class="form-label small fw-semibold">Year:</label>
                    <select class="form-select form-select-sm" v-model="filters.year">
                      <option value="">Select Year</option>
                      <option v-for="year in years" :key="year" :value="year">{{ year }}</option>
                    </select>
                  </div>

                  <div class="col-md-2 d-flex align-items-center">
                    <div class="w-100">
                      <label class="form-label small fw-semibold invisible">Action</label>
                      <button class="btn btn-sm btn-outline-secondary w-100" @click="clearFilters">
                        Clear Filters
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Table Controls -->
              <div class="table-controls">
                <div class="table-controls-top">
                  <div class="table-controls-left">
                    <div class="entries-control">
                      <span>Show</span>
                      <select class="form-select form-select-sm" v-model.number="pageLength">
                        <option :value="10">10</option>
                        <option :value="25">25</option>
                        <option :value="50">50</option>
                        <option :value="100">100</option>
                      </select>
                      <span>Entries</span>
                    </div>

                    <div class="search-box d-flex w-100">
                      <span class="material-symbols-outlined search-icon">search</span>
                      <input type="search" v-model="search" class="form-control form-control-sm search-input"
                        placeholder="Search transactions..." />
                    </div>
                  </div>

                  <div class="d-flex align-items-center mt-2">
                    <input type="checkbox" @change="toggleShowDeductions" id="deductionsCheck"
                      class=" d-flex me-1 align-items-center" :checked="showDeductions" />
                    <span>Show Deductions</span>
                  </div>

                  <div class="export-wrapper"
                    :title="paginatedItems.length === 0 ? 'No data to export' : 'Export current view to Excel'">
                    <button class="btn btn-md btn-success w-100 d-flex justify-content-between align-items-center"
                      @click="exportData" :disabled="paginatedItems.length === 0">
                      <span class="material-symbols-outlined align-items-center me-2">download</span>
                      Export
                    </button>
                  </div>
                </div>

              </div>

              <!-- Table -->
              <div class="table-wrapper">
                <div class="table-container">
                  <table class="cashbook-table" style="width: 100%; white-space: nowrap;">
                    <thead>
                      <tr>
                        <th rowspan="2">DATE</th>
                        <th rowspan="2">PAYEE</th>
                        <th rowspan="2">PARTICULARS</th>
                        <th rowspan="2">TYPE</th>
                        <th rowspan="2">BANK</th>
                        <th rowspan="2" class="amount-col">INFLOW</th>
                        <th rowspan="2" class="amount-col">GROSS</th>
                        <th rowspan="2" class="amount-col">OUTFLOW (NET)</th>
                        <th rowspan="2" class="amount-col">BALANCE</th>
                        <th colspan="6" class="deductions-header text-align-center" v-if="showDeductions">DEDUCTIONS
                        </th>
                        <!-- <th rowspan="2">Action</th> -->
                      </tr>
                      <tr>
                        <th class="amount-col" v-if="showDeductions">ADMIN CHARGE</th>
                        <th class="amount-col" v-if="showDeductions">FG W/T</th>
                        <th class="amount-col" v-if="showDeductions">VAT</th>
                        <th class="amount-col" v-if="showDeductions">STATE W/T</th>
                        <th class="amount-col" v-if="showDeductions">OTHERS</th>
                        <th class="amount-col" v-if="showDeductions">TOTAL</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-if="paginatedItems.length === 0">
                        <td colspan="15" class="text-center text-muted">No entries to display.</td>
                      </tr>
                      <tr v-for="(transaction, index) in paginatedItems" :key="transaction.id"
                        :class="index % 2 === 0 ? 'row-even' : 'row-odd'">
                        <td>{{ formatDate(transaction.createdDate) }}</td>
                        <td>{{ transaction.payee }}</td>
                        <td>
                          <router-link :to="{ name: 'CashBookEntry', params: { id: transaction.id } }"
                            class="view-link text-decoration-underline" title="View Cashbook Entry">
                            {{ transaction.particulars }}
                          </router-link>
                        </td>
                        <td>{{ transaction.natureOfExpenditure }}</td>
                        <td>{{ transaction.bankName }}</td>
                        <td class="amount-col">{{ formatCurrency(transaction.inflow) }}</td>
                        <td class="amount-col">{{ formatCurrency(transaction.grossAmount) }}</td>
                        <td class="amount-col">{{ formatCurrency(transaction.netOutflow) }}</td>
                        <td class="amount-col fw-semibold">{{ formatCurrency(transaction.balance) }}</td>
                        <td class="amount-col" v-if="showDeductions">
                          {{ formatCurrency(getDeductionMap(transaction.deductions).admin) }}
                        </td>

                        <td class="amount-col" v-if="showDeductions">
                          {{ formatCurrency(getDeductionMap(transaction.deductions).fgwt) }}
                        </td>
                        <td class="amount-col" v-if="showDeductions">
                          {{ formatCurrency(getDeductionMap(transaction.deductions).vat) }}
                        </td>
                        <td class="amount-col" v-if="showDeductions">
                          {{ formatCurrency(getDeductionMap(transaction.deductions).statewt) }}
                        </td>
                        <td class="amount-col" v-if="showDeductions">
                          {{ formatCurrency(getDeductionMap(transaction.deductions).others) }}
                        </td>
                        <td class="amount-col fw-semibold" v-if="showDeductions">
                          {{ formatCurrency(transaction.totalDeduction) }}
                        </td>
                        <!-- <td data-label="Action" class="action-col">
                          <router-link :to="{ name: 'CashBookEntry', params: { id: transaction.id } }"
                            class="view-link text-decoration-none">
                            View
                          </router-link>
                        </td> -->
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Pagination Summary and Actions -->
              <div class="pagination-info">
                <small>
                  Showing {{ (currentPage - 1) * pageLength + 1 }} to
                  {{ Math.min(currentPage * pageLength, filteredItems.length) }} of
                  {{ filteredItems.length }} entries
                </small>

                <!-- Pagination Controls -->
                <nav class="pagination-nav">
                  <ul class="pagination pagination-sm mb-0">
                    <li class="page-item" :class="{ disabled: currentPage === 1 }">
                      <button class="page-link" @click.prevent="changePage(currentPage - 1)">Previous</button>
                    </li>
                    <li v-for="page in displayedPages" :key="page" class="page-item"
                      :class="{ active: currentPage === page }">
                      <button class="page-link" @click.prevent="changePage(page)">{{ page }}</button>
                    </li>
                    <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                      <button class="page-link" @click.prevent="changePage(currentPage + 1)">Next</button>
                    </li>
                  </ul>
                </nav>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  </DashboardLayout>
</template>

<script setup>
import { ref, computed, reactive, onMounted, watch } from 'vue'
import axios from 'axios'
import Cookies from 'js-cookie'
import DashboardLayout from '../layout/DashboardLayout.vue'
import SearchableSelect from '../common/SearchableSelect.vue'
import { debounce } from 'lodash'
import ExcelJS from 'exceljs'
import '@/assets/css/table.css'
import '@/assets/css/responsive-table.css'

const transactions = ref([])
const mdas = ref([])
const banks = ref([])
// const loading = ref(false)
const loadingCashbook = ref(true)
const errorMessage = ref('')
const loadingBanks = ref(false)
const loadingMDAs = ref(false)
const search = ref('')
const pageLength = ref(10)
const currentPage = ref(1)
const root_api_url = process.env.VUE_APP_API_ROOT_URL
const showDeductions = ref(false)
const now = new Date()

const months = [
  { value: '1', label: 'January' },
  { value: '2', label: 'February' },
  { value: '3', label: 'March' },
  { value: '4', label: 'April' },
  { value: '5', label: 'May' },
  { value: '6', label: 'June' },
  { value: '7', label: 'July' },
  { value: '8', label: 'August' },
  { value: '9', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' }
]

const years = computed(() => {
  const currentYear = now.getFullYear()
  const yearsList = []
  for (let i = 0; i <= 5; i++) {
    yearsList.push(currentYear - i)
  }
  return yearsList
})

const currentMonth = (now.getMonth() + 1).toString() // 1-12
const currentYear = now.getFullYear().toString()

const mdaFilterOptions = computed(() =>
  [...mdas.value]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(mda => ({ id: mda.name, name: mda.name }))
)

const onMdaFilterChange = (val) => {
  filters.value.mda = val
  onMdaChange()
}

function onMdaChange() {
  // console.log('MDA changed to', mda.value)
  const selected = mdas.value.find(item => item.name === filters.value.mda)
  form.mdaId = selected ? selected.id : ''
  console.log('Updated form.mdaId to', form.mdaId)
}

function onBankChange() {
  // console.log('Bank changed to', bank.value)
  const selected = banks.value.find(item => item.name === filters.value.bank)
  form.bankId = selected ? selected.id : ''
  console.log('Updated form.bankId to', form.bankId)

}
const filters = ref({
  mda: '',
  bank: '',
  month: currentMonth,
  year: currentYear
})

const toggleShowDeductions = () => {
  showDeductions.value = !showDeductions.value
}

const debouncedSearch = ref('')
watch(search, debounce((val) => {
  debouncedSearch.value = val
  currentPage.value = 1
}, 300))

watch(pageLength, () => {
  fetchTransactions()
  currentPage.value = 1
})

watch(filters, () => {
  fetchTransactionsByFilter()
  currentPage.value = 1
}, { deep: true })

// const selectCustomPeriod = (value) => {
//   if (value === 'custom') {
//     filters.value.startDate = ''
//     filters.value.endDate = ''
//   }
// }

const form = reactive({
  id: '',
  createdBy: '',
  lastModifiedBy: '',
  userId: '',
  mdaId: '',
  bankId: '',
  payee: '',
  mdaName: '',
  bankName: '',
  particulars: '',
  natureOfExpenditure: '',
  warrantNumber: '',
  pvId: '',
  chequeNumber: '',
  inflow: '',
  netOutflow: '',
  balance: '',
  grossAmount: '',
  totalDeduction: '',
  deductions: {
    id: '',
    dedTypeId: '',
    dedTypeName: '',
    paymentVoucherId: '',
    amount: '',
    dedTypeAmt: '',
    dedTypeMode: '',
  },
  createdDate: '',
  lastModifiedDate: '',
  errorMessage: '',
})


const getDeductionMap = (deductions) => {
  const map = {
    admin: 0,
    fgwt: 0,
    vat: 0,
    statewt: 0,
    others: 0
  }

  deductions.forEach(d => {
    const name = (d.dedTypeName || '').toLowerCase()

    if (name.includes('admin')) map.admin = Number(d.amount)
    else if (name.includes('fg')) map.fgwt = Number(d.amount)
    else if (name.includes('vat')) map.vat = Number(d.amount)
    else if (name.includes('state')) map.statewt = Number(d.amount)
    else map.others += Number(d.amount)
  })

  return map
}

const fetchTransactions = async () => {
  const token = Cookies.get('auth_token')
  loadingCashbook.value = true
  errorMessage.value = ''
  try {
    const response = await axios.get(`${root_api_url}/payment/cashbook/get-all`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      params: {
        page: currentPage.value,
        size: pageLength.value,
        month: parseInt(filters.value.month),
        year: parseInt(filters.value.year)
      }
    })

    const apiData = response.data.data
    const data = Array.isArray(apiData.data) ? apiData.data : []

    transactions.value = data.map(transaction => ({
      id: transaction.id,
      createdBy: transaction.createdBy,
      lastModifiedBy: transaction.lastModifiedBy,
      userId: transaction.userId,
      mdaId: transaction.mdaId,
      bankId: transaction.bankId,
      payee: transaction.payee,
      mdaName: transaction.mdaName,
      bankName: transaction.bankName,
      particulars: transaction.particulars,
      natureOfExpenditure: transaction.natureOfExpenditure,
      warrantNumber: transaction.warrantNumber,
      pvId: transaction.pvId,
      pvNumber: transaction.pvNumber,
      chequeNumber: transaction.chequeNumber,
      inflow: transaction.inflow,
      netOutflow: transaction.netOutflow,
      balance: transaction.balance,
      grossAmount: transaction.grossAmount,
      totalDeduction: transaction.totalDeduction,
      deductions: transaction.deductions || [],
      createdDate: transaction.createdDate,
      lastModifiedDate: transaction.lastModifiedDate,
    }))

    console.log('Fetched transactions:', transactions.value)

  } catch (error) {
    console.error('Failed to fetch transactions:', error)
    console.error('Error details:', error.response?.data) // Log the actual error
    transactions.value = []
  } finally {
    loadingCashbook.value = false
  }

}

const fetchTransactionsByFilter = async () => {
  loadingCashbook.value = true
  errorMessage.value = ''
  const token = Cookies.get('auth_token')
  try {
    const response = await axios.get(`${root_api_url}/payment/cashbook/filter`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      params: {
        mdaId: form.mdaId || '',
        bankId: form.bankId || '',
        page: currentPage.value,
        size: pageLength.value,
        month: parseInt(filters.value.month),
        year: parseInt(filters.value.year)
      }
    })

    const apiData = response.data.data
    const data = Array.isArray(apiData.data) ? apiData.data : []

    transactions.value = data.map(transaction => ({
      id: transaction.id,
      createdBy: transaction.createdBy,
      lastModifiedBy: transaction.lastModifiedBy,
      userId: transaction.userId,
      mdaId: transaction.mdaId,
      bankId: transaction.bankId,
      payee: transaction.payee,
      mdaName: transaction.mdaName,
      bankName: transaction.bankName,
      particulars: transaction.particulars,
      natureOfExpenditure: transaction.natureOfExpenditure,
      warrantNumber: transaction.warrantNumber,
      pvId: transaction.pvId,
      pvNumber: transaction.pvNumber,
      chequeNumber: transaction.chequeNumber,
      inflow: transaction.inflow,
      netOutflow: transaction.netOutflow,
      balance: transaction.balance,
      grossAmount: transaction.grossAmount,
      totalDeduction: transaction.totalDeduction,
      deductions: transaction.deductions || [],
      createdDate: transaction.createdDate,
      lastModifiedDate: transaction.lastModifiedDate,
    }))
  } catch (error) {
    console.error('Failed to fetch transactions:', error)
    console.error('Error details:', error.response?.data) // Log the actual error
    transactions.value = []
  } finally {
    loadingCashbook.value = false
  }

}

const fetchMDAs = async () => {
  loadingMDAs.value = true

  const token = Cookies.get('auth_token')
  try {
    const response = await axios.get(`${root_api_url}/eli/mda`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    const data = Array.isArray(response.data) ? response.data : []

    mdas.value = data.map(mda => ({
      id: mda.id,
      name: mda.name,
    }))
  } catch (error) {
    mdas.value = []
    console.error('Failed to fetch mdas:', error)
  } finally {
    loadingMDAs.value = false
  }
}


const fetchBanks = async () => {
  loadingBanks.value = true
  const token = Cookies.get('auth_token')
  try {
    const response = await axios.get(`${root_api_url}/payment/banks`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    const data = Array.isArray(response.data) ? response.data : []

    banks.value = data.map(bank => ({
      id: bank.id,
      name: bank.bankName || ''
    }))
  } catch (error) {
    banks.value = []
    console.error('Failed to fetch banks:', error)
  } finally {
    loadingBanks.value = false
  }
}

const uniqueBanks = computed(() => {
  return [...banks.value].sort((a, b) => a.name.localeCompare(b.name))
})


const filteredItems = computed(() => {
  let filtered = [...transactions.value]

  // Apply search filter
  if (debouncedSearch.value) {
    const term = debouncedSearch.value.toLowerCase()
    filtered = filtered.filter(transaction => {
      return (
        transaction.payee?.toLowerCase().includes(term) ||
        transaction.particulars?.toLowerCase().includes(term) ||
        transaction.natureOfExpenditure?.toLowerCase().includes(term) ||
        transaction.chequeNumber?.toLowerCase().includes(term) ||
        transaction.warrantNumber?.toLowerCase().includes(term) ||
        transaction.pvNumber?.toLowerCase().includes(term)
      )
    })
  }

  // Apply date sorting (MOST RECENT FIRST)
  filtered = [...filtered].sort((a, b) => {
    const dateA = new Date(a.createdDate || 0)
    const dateB = new Date(b.createdDate || 0)
    return dateB - dateA
  })


  return filtered
})

const paginatedItems = computed(() => {
  const start = (currentPage.value - 1) * pageLength.value
  return filteredItems.value.slice(start, start + pageLength.value)
})

const totalPages = computed(() => {
  return Math.ceil(filteredItems.value.length / pageLength.value)
})

const displayedPages = computed(() => {
  const pages = []
  const maxDisplayed = 5
  let startPage = Math.max(1, currentPage.value - Math.floor(maxDisplayed / 2))
  let endPage = Math.min(totalPages.value, startPage + maxDisplayed - 1)

  if (endPage - startPage < maxDisplayed - 1) {
    startPage = Math.max(1, endPage - maxDisplayed + 1)
  }

  for (let i = startPage; i <= endPage; i++) {
    pages.push(i)
  }

  return pages
})

const changePage = (page) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page
  }
}

const clearFilters = () => {
  filters.value.mda = ''
  filters.value.bank = ''
  filters.value.month = currentMonth
  filters.value.year = currentYear
  search.value = ''
  fetchTransactions()
}

const formatDate = (dateString) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  })
}

const formatCurrency = (amount) => {
  if (!amount || amount === 0) return '₦0.00'
  return '₦' + amount.toLocaleString('en-NG', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
}

const exportData = async () => {
  const workbook = new ExcelJS.Workbook()
  const worksheet = workbook.addWorksheet('Cashbook')

  const headerFill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FF157347' }
  }

  const headerFont = {
    bold: true,
    color: { argb: 'FFFFFFFF' }
  }

  // Title rows — merged A:S (19 cols), value on A (top-left of merge)
  worksheet.mergeCells('A1:S1')
  worksheet.mergeCells('A2:S2')
  worksheet.mergeCells('A3:S3')
  worksheet.mergeCells('A4:S4')

  worksheet.getCell('A1').value = 'OGUN STATE GOVERNMENT OF NIGERIA'

  const titles = []
  titles.push(`PERIOD: ${months[currentMonth - 1].label}, ${currentYear}`)
  if (filters.value.bank) titles.unshift(`BANK: ${filters.value.bank}`)
  if (filters.value.mda) titles.unshift(`MDA: ${filters.value.mda}`)

  titles.forEach((text, i) => {
    worksheet.getCell(`A${i + 2}`).value = text
  })

  // Two-row column headers: rows 5–6
  // A–L: span both rows (merge 5:6)
  // M–S: DEDUCTIONS group in row 5, sub-headers in row 6
  const singleHeaders = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L']
  singleHeaders.forEach(col => {
    worksheet.mergeCells(`${col}5:${col}6`)
  })

  // DEDUCTIONS group header spans M5:S5
  worksheet.mergeCells('M5:S5')

  // Row 5 — main headers
  worksheet.getCell('A5').value = 'DATE'
  worksheet.getCell('B5').value = 'AGENCY'
  worksheet.getCell('C5').value = 'PAYEE'
  worksheet.getCell('D5').value = 'PARTICULARS'
  worksheet.getCell('E5').value = 'TYPE'
  worksheet.getCell('F5').value = 'R/WARRANT'
  worksheet.getCell('G5').value = 'PV NO'
  worksheet.getCell('H5').value = 'BANK'
  worksheet.getCell('I5').value = 'CHQ NO'
  worksheet.getCell('J5').value = 'INFLOW'
  worksheet.getCell('K5').value = 'OUTFLOW (NET)'
  worksheet.getCell('L5').value = 'BALANCE'
  worksheet.getCell('M5').value = 'DEDUCTIONS'

  // Row 6 — deduction sub-headers (start at col 13 = M)
  const deductionHeaders = [
    'ADMIN CHARGE', 'FG W/T', 'VAT', 'STATE W/T', 'OTHERS', 'GROSS', 'REMARK'
  ]
  deductionHeaders.forEach((header, index) => {
    worksheet.getCell(6, 13 + index).value = header
  })

  worksheet.properties.defaultRowHeight = 20
  worksheet.views = [{ state: 'frozen', ySplit: 6 }]
  worksheet.columns.forEach(col => { col.width = 20 })

  // Export ALL filtered data, not just current page
  filteredItems.value.forEach(transaction => {
    const row = worksheet.addRow([
      formatDate(transaction.createdDate),                              // A  col 1
      transaction.mdaName,                                              // B  col 2
      transaction.payee,                                                // C  col 3
      transaction.particulars,                                          // D  col 4
      transaction.natureOfExpenditure,                                  // E  col 5
      transaction.warrantNumber,                                        // F  col 6
      transaction.pvNumber,                                             // G  col 7
      transaction.bankName,                                             // H  col 8
      transaction.chequeNumber,                                         // I  col 9
      Number(transaction.inflow || 0),                                  // J  col 10
      Number(transaction.netOutflow || 0),                              // K  col 11
      Number(transaction.balance || 0),                                 // L  col 12
      Number(getDeductionMap(transaction.deductions).admin || 0),       // M  col 13
      Number(getDeductionMap(transaction.deductions).fgwt || 0),        // N  col 14
      Number(getDeductionMap(transaction.deductions).vat || 0),         // O  col 15
      Number(getDeductionMap(transaction.deductions).statewt || 0),     // P  col 16
      Number(getDeductionMap(transaction.deductions).others || 0),      // Q  col 17
      Number(transaction.grossAmount || 0),                             // R  col 18
      transaction.remark                                                // S  col 19
    ])

    // Currency cols: J–R (10–18)
    const currencyCols = [10, 11, 12, 13, 14, 15, 16, 17, 18]
    currencyCols.forEach(col => {
      worksheet.getColumn(col).width = 20
      const cell = row.getCell(col)
      cell.value = Number(cell.value || 0)
      cell.numFmt = '#,##0.00'
      cell.alignment = { horizontal: 'right' }
    })
  })

  // Row heights
  worksheet.getRow(1).height = 25
  worksheet.getRow(2).height = 20
  worksheet.getRow(3).height = 20
  worksheet.getRow(4).height = 20
  worksheet.getRow(5).height = 18
  worksheet.getRow(6).height = 18

  // Header styles — rows 1–6, cols 1–19
  for (let row = 1; row <= 6; row++) {
    for (let col = 1; col <= 19; col++) {
      const cell = worksheet.getCell(row, col)
      cell.fill = headerFill
      cell.font = headerFont
      cell.alignment = { horizontal: 'center', vertical: 'middle' }
    }
  }

  const buffer = await workbook.xlsx.writeBuffer()
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url

  let filename = ''
  if (filters.value.mda && filters.value.bank) {
    filename = `${filters.value.mda}_${filters.value.bank}_${months[currentMonth - 1].label}_${currentYear}_cashbook`
  } else if (filters.value.mda) {
    filename = `${filters.value.mda}_${months[currentMonth - 1].label}_${currentYear}_cashbook`
  } else if (filters.value.bank) {
    filename = `${filters.value.bank}_${months[currentMonth - 1].label}_${currentYear}_cashbook`
  } else {
    filename = `${months[currentMonth - 1].label}_${currentYear}_cashbook`
  }

  link.download = filename.toUpperCase() + '.xlsx'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

onMounted(async () => {
  await fetchMDAs()
  await fetchBanks()
  await fetchTransactions()
  document.title = "Cashbook"
})

</script>

<style scoped>
.cashbook-content-area {
  min-height: 360px;
}

.loading-overlay {
  position: absolute;
  inset: 0;
  z-index: 20;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  pointer-events: all;
}

/* Fix horizontal scroll - override any parent overflow restrictions */
.view-item,
.form-inner,
.form-body {
  overflow: visible !important;
}

.form-outer {
  width: 100%;
  max-width: 100% !important;
}

/* Component-specific styles */
.primary-col {
  font-weight: 500;
  color: #212529;
}

.row-even {
  background-color: #ffffff;
}

.row-odd {
  background-color:var(--table-bg-head);
}

.material-symbols-outlined {
  font-size: 1.25rem;
}

.filters-section {
  background-color:var(--table-bg-head);
  padding: 1.5rem;
  border-radius: 0.375rem;
  border: 1px solid #dee2e6;
}

.form-label {
  margin-bottom: 0.25rem;
    color: var(--color);
}

.showing-info {
  padding-left: 0.5rem;
}

.export-wrapper {
  display: flex;
  align-items: center;
}

/* Wide table container for cashbook */
.table-container-wide {
  width: 100%;
  overflow-x: auto;
}

.cashbook-table {
  width: 100%;
  min-width: 1800px;
  border-collapse: collapse;
  font-size: 0.85rem;
}

.cashbook-table thead {
  background-color:var(--table-bg-head);
  position: sticky;
  top: 0;
  z-index: 10;
}

.cashbook-table th {
  padding: 0.75rem 0.5rem;
  text-align: left;
  font-weight: 600;
  font-size: 0.75rem;
  color: #1f2937;
  text-transform: uppercase;
  border: 1px solid #dee2e6;
  background-color:var(--table-bg-head);
  white-space: nowrap;
}

.cashbook-table th.deductions-header {
  text-align: center;
  background-color: var(--hover);
}

.cashbook-table td {
  padding: 0.75rem 0.5rem;
  border: 1px solid #dee2e6;
  color: #374151;
  white-space: nowrap;
}

.cashbook-table .amount-col {
  text-align: right;
}

.cashbook-table tbody tr:hover {
  background-color: #f3f4f6;
}

.text-center {
  text-align: center;
  padding: 2rem;
  color: #6b7280;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .filters-section .row {
    gap: 0.75rem;
  }

  .table-controls-top {
    flex-direction: column;
    gap: 1rem;
  }
}
</style>
